/**
 * Dancer visibility SCOPES — the directory-level adapter over the field
 * registry
 * ============================================================================
 *
 * `view-fields.ts` is the fine-grained matrix: which profile field is visible
 * on which surface, for which brand, to which viewer role. This module is the
 * COARSE adapter the dancer-listing surfaces speak: a single `VisibilityScope`
 * that a server loader computes once and hands to the row mapper.
 *
 * Why a second, thinner layer (and why it does NOT duplicate the matrix):
 *   - List/directory loaders don't want to reason field-by-field. They want
 *     "render this row for a Fever staffer" vs "render it for a peer dancer."
 *   - The privacy lock (BAL-316, R4) is a coarse, cross-surface rule: peer &
 *     public scopes NEVER carry cast-frequency signals, on ANY surface.
 *   - So this module maps a scope → a `ViewContext` and DERIVES every
 *     field decision from `view-fields.isFieldVisible`. It owns no field list
 *     of its own beyond re-using `CAST_SIGNAL_FIELDS` from the registry. One
 *     matrix, one decision point (Critical Rule #8).
 *
 * The five scopes:
 *   - `fever_staff`  — a Fever client-org member on the directory. Sees cast
 *                      signals.
 *   - `ballee_admin` — super admin. Operational parity with Fever.
 *   - `dancer_self`  — a dancer viewing their OWN row. (Own-count exposure is
 *                      a MAY per the contract, flagged to Phil, Q6; today the
 *                      mapper strips signals here too — the safe reading — and
 *                      the data source `get_fever_cast_metrics` is Fever-gated
 *                      so a dancer can't reach counts anyway.)
 *   - `dancer_peer`  — a dancer viewing ANOTHER dancer. Cast signals STRIPPED.
 *                      This is the leaderboard anti-pattern Philippine flagged.
 *   - `public`       — anonymous / logged-out. Cast signals STRIPPED.
 *
 * Server-side only: the scope is computed from the auth session + membership,
 * never passed by the client. A forged `scope` from the browser cannot widen
 * exposure because the loader recomputes it.
 */

import {
  CAST_SIGNAL_FIELDS,
  isFieldVisible,
  type Brand,
  type ViewContext,
  type ViewerRole,
} from './view-fields';

export type VisibilityScope =
  | 'fever_staff'
  | 'ballee_admin'
  | 'dancer_self'
  | 'dancer_peer'
  | 'public';

/**
 * Scope → viewer role. The `fever_directory` surface's field rules key off
 * the viewer role, so this mapping is what makes the cast-signal gate
 * (`FEVER_OR_ADMIN`) fire correctly per scope.
 */
const SCOPE_TO_VIEWER: Record<VisibilityScope, ViewerRole> = {
  fever_staff: 'fever_staff',
  ballee_admin: 'admin',
  dancer_self: 'owner',
  dancer_peer: 'authenticated',
  public: 'anonymous',
};

/**
 * Build the fine-grained `ViewContext` for a scope. Every directory scope
 * renders the `fever_directory` surface — that surface's field set defines the
 * directory card; the viewer role gates the cast signals within it.
 */
export function scopeToViewContext(
  scope: VisibilityScope,
  brand: Brand = 'flow',
): ViewContext {
  return {
    surface: 'fever_directory',
    brand,
    viewer: SCOPE_TO_VIEWER[scope],
  };
}

/**
 * True when the scope is permitted to carry cast-frequency signals. Derived
 * from the ONE registry: a signal field is allowed iff the registry says it's
 * visible for this scope's viewer on the fever_directory surface. Because
 * every `CAST_SIGNAL_FIELD` is `FEVER_OR_ADMIN`, this is true only for
 * `fever_staff` and `ballee_admin` — the R4 lock, expressed once.
 */
export function scopeAllowsCastSignals(scope: VisibilityScope): boolean {
  const ctx = scopeToViewContext(scope);
  return CAST_SIGNAL_FIELDS.every((field) => isFieldVisible(field, ctx));
}

/**
 * The server-side row mapper. Takes a dancer row that MAY carry cast-signal
 * fields and returns a copy with every signal the scope is not allowed to see
 * removed. Peer & public scopes come back with zero cast signals — the leak is
 * impossible downstream because the field is not on the object at all.
 *
 * Signal-field keys on the row are expected to match the `ProfileFieldKey`
 * camelCase names (`totalFeverShows`, `lastCastDate`, ...). Non-signal fields
 * pass through untouched — this mapper's ONLY job is the cast-signal strip;
 * per-field profile visibility (contact, measurements, ...) stays in
 * `isFieldVisible` at the render site.
 */
export function dancerFieldsForScope<T extends object>(
  scope: VisibilityScope,
  row: T,
): T {
  const ctx = scopeToViewContext(scope);
  const blocked = new Set<string>(
    CAST_SIGNAL_FIELDS.filter((field) => !isFieldVisible(field, ctx)),
  );

  const out = Object.fromEntries(
    Object.entries(row).filter(([key]) => !blocked.has(key)),
  );

  // Returns `T`: the only keys removed are cast-signal fields, which are
  // OPTIONAL on every directory row type (e.g. FeverDirectoryDancer), so a
  // stripped row still satisfies T. The mapper is a filter, not a reshape —
  // this keeps loaders type-clean instead of forcing a Partial<T> downstream.
  return out as T;
}

/**
 * The cast-signal field keys, re-exported for the leak-guard test and for
 * loaders that need to know which columns to request only under an allowed
 * scope. Sourced from the registry — never redefine this list here.
 */
export { CAST_SIGNAL_FIELDS };
